/**
 * SK360 — THE GANN 360-DEGREE PRICE-TIME PROJECTION.
 *
 * From one clicked bar, where price "should" be after one full turn of the square-of-nine wheel:
 *
 *   C = the anchor bar's close        R = sqrt(C)
 *   upper = (R + 2)^2                 one turn up
 *   lower = (R - 2)^2                 one turn down, clamped at 0
 *   T     = the anchor's date + 1 calendar year
 *
 * and two line segments from (anchorDate, C) out to (T, upper) and (T, lower).
 *
 * <h2>WHY THE STEP IS 2 AND NOT 1</h2>
 *
 * On the square-of-nine, consecutive squares of the odd and even roots sit one full revolution
 * apart: the root advances by 2 for every 360 degrees travelled. So the step is a property of the
 * wheel rather than a tuning parameter, which is why it is a named constant and not an argument.
 * The arithmetic expands to {@code upper = C + 4*sqrt(C) + 4} and {@code lower = C - 4*sqrt(C) + 4},
 * so the two targets are {@code 8*sqrt(C)} apart and the pair is NOT symmetric about C — both are
 * shifted up by 4. That asymmetry is in the formula, not a mistake in it.
 *
 * <h2>⚠ THE LOWER CLAMP EXISTS BECAUSE SQUARING A NEGATIVE ROOT FLIPS THE LINE UPWARD</h2>
 *
 * Below C = 4 the root is under 2, so {@code R - 2} is negative and squaring it produces a
 * POSITIVE number that can sit above the anchor: a close of 0.25 gives a root of 0.5 and
 * {@code (0.5 - 2)^2 = 2.25}, nine times the anchor price. Drawn unclamped, the "one turn down"
 * line would rise — the opposite of what it claims, on exactly the penny stocks where a reader is
 * least able to sanity-check it. Clamping to 0 says "a full turn down takes this to nothing",
 * which is the honest reading of a wheel that has run out of root.
 *
 * <h2>⚠ A CLOSE OF ZERO PRODUCES NOTHING, and that is a real case in this data</h2>
 *
 * HPIL carries 2,089 parseable all-zero rows and last traded at 0.0001, and the repaired `nu` rows
 * added 597 more. A zero close has no root to turn, so it yields no projection rather than a
 * confident line from 0 to 4 — a figure derived from the absence of a price.
 *
 * <h2>The year is SHARED ARITHMETIC and its own constant</h2>
 *
 * {@code yearsAfter} is borrowed from the projection-ceiling module because the leap-day rule is
 * worth having in one place (29 February plus a year CLAMPS to the 28th, where
 * {@code setFullYear} overflows into March). The COUNT is this module's own: the ceiling's year is
 * an entitlement and this one is a 360-degree cycle, and coupling them would make a change to the
 * admin horizon silently turn SK360 into a 720-degree projection.
 */

import { weekdaysBetween } from './chartEndDates';
import { isoDay, yearsAfter } from './getChartFutureCeilingDate';
import { indexAtOrAfter } from './timeBandOverlay';

/** Every overlay this tool creates is prefixed, so toggling can find and remove them. */
export const SK360_PREFIX = 'sk360-';

/** Root units travelled per 360 degrees of the wheel. A property of the square, not a setting. */
export const SK360_ROOT_STEP = 2;

/** One full turn, in years of calendar time. See the note on sharing `yearsAfter`. */
export const SK360_YEARS_AHEAD = 1;

/**
 * ⚠ DARK GOLDENROD, AND THE BRIEF'S OWN TWO SUGGESTIONS BOTH FAIL THE CONTRAST FLOOR.
 *
 * Measured against this app's two canvases ({@code CANVAS_LIGHT} #f9fafe, {@code CANVAS_DARK}
 * #0d1117), against the 3:1 a graphical object needs to register at all:
 *
 * <pre>
 *   goldenrod      #DAA520   2.15 light   8.46 dark   FAILS light
 *   cyan           #06B6D4   2.33 light   7.79 dark   FAILS light
 *   dark goldenrod #B8860B   3.12 light   5.82 dark   clears both
 * </pre>
 *
 * So the brief's `goldenrod` is invisible on the light theme and its `cyan` is both invisible
 * there AND is {@code PTB_COLOR} — it would collide with the PTB bands on a chart that can carry
 * both. #B8860B is the only goldenrod that reads on both, and it is already this app's goldenrod
 * (the pivot-commonality study uses it), so this adds no ninth hue to a chart that has eight.
 */
export const SK360_COLOR = '#B8860B';

/** Upper solid, lower dashed — so the pair survives a monochrome screenshot. */
export const SK360_UPPER_LINE_TYPE = 'solid';
export const SK360_LOWER_LINE_TYPE = 'dashed';
export const SK360_LINE_WIDTH = 2;

/**
 * The two price targets for one anchor close.
 *
 * @param {number} close the anchor bar's close
 * @returns {{close:number, root:number, upper:number, lower:number, clamped:boolean}|null}
 *          null when there is no positive price to take a root of
 */
export function sk360Targets(close) {
  const c = Number(close);
  // Not `< 0`: zero has no root to turn, and it is a value this store really holds. See the
  // header note on HPIL.
  if (!Number.isFinite(c) || c <= 0) return null;

  const root = Math.sqrt(c);
  const rootBelow = root - SK360_ROOT_STEP;
  // The clamp is on the SQUARE, decided by the sign of the ROOT — squaring first would lose the
  // sign and the test could never fire.
  const clamped = rootBelow < 0;

  return {
    close: c,
    root,
    upper: (root + SK360_ROOT_STEP) ** 2,
    lower: clamped ? 0 : rootBelow ** 2,
    clamped,
  };
}

/** One full turn ahead of {@code anchorDate}, as an ISO day. Null when the date is unreadable. */
export function sk360TargetDate(anchorDate) {
  return yearsAfter(isoDay(anchorDate), SK360_YEARS_AHEAD);
}

/**
 * WHERE THE SEGMENT ENDS ON THE AXIS, and the column it WOULD have ended on.
 *
 * <p>⚠ TWO CASES, because the axis is not uniform. Real bars skip exchange holidays (~9 a year)
 * while {@code extendBarData} pads with every weekday — so a single weekday count from the anchor
 * overshoots by the holidays in between, about 3.5% of a year. Resolved separately:
 *
 * <ul>
 *   <li>the target lands ON the axis — {@code indexAtOrAfter} gives the exact column, holidays and
 *       padding both accounted for, because it searches the real list;</li>
 *   <li>the target is PAST the axis — counted in weekdays from the last REAL bar, which is exact
 *       because from there on the axis is pure weekdays by construction.</li>
 * </ul>
 *
 * @param {string[]} axisDates     the padded axis
 * @param {number}   realBarCount  how many of those columns carry a real bar
 * @param {string}   targetDate    the ISO day the segment aims at
 * @returns {{index:number, ideal:number, truncated:boolean}|null}
 */
export function sk360Terminus(axisDates, realBarCount, targetDate) {
  if (!Array.isArray(axisDates) || axisDates.length === 0) return null;
  const target = isoDay(targetDate);
  if (!target) return null;

  const onAxis = indexAtOrAfter(axisDates, target);
  if (onAxis >= 0) return { index: onAxis, ideal: onAxis, truncated: false };

  const lastReal = Math.min(Math.max(0, (realBarCount || axisDates.length) - 1),
    axisDates.length - 1);
  const ahead = weekdaysBetween(axisDates[lastReal], target);
  return { index: axisDates.length - 1, ideal: lastReal + ahead, truncated: true };
}

/**
 * The value of a segment at the column it was cut off at.
 *
 * <p>⚠ INTERPOLATED IN BAR-INDEX SPACE, not in calendar days. The x axis is a CATEGORY axis — one
 * column per bar — so ECharts draws the segment linearly between columns and the visible slope is
 * per-column. Interpolating in days would truncate the line at a price its own drawn angle does
 * not pass through.
 *
 * <p>Truncating rather than redirecting the line to the axis edge is the whole point: the angle IS
 * the projection, so moving the terminus would show a different projection at full length instead
 * of this one partly. The caller reports the real target beside the drawn end.
 */
export function sk360PriceAt(anchorIndex, anchorClose, idealIndex, idealPrice, atIndex) {
  const run = idealIndex - anchorIndex;
  if (!Number.isFinite(run) || run === 0) return idealPrice;
  const frac = (atIndex - anchorIndex) / run;
  return anchorClose + (idealPrice - anchorClose) * frac;
}

/**
 * Builds SK360's overlays for one anchor bar.
 *
 * <p>Admin is re-checked here rather than only on the control, for the reason every study in this
 * directory states: a hidden button is a convenience and not a guard.
 *
 * <p>⚠ ONE {@code addOverlay} CALL IS WHAT REGISTERS IT WITH X1 AND X. {@code ChartOverlayContext}
 * batches every add made in the same tick under one {@code groupId}, and undo removes a whole
 * group — so returning both segments together makes the pair a single undoable figure. Added
 * separately they would undo one line at a time, leaving half a projection on the chart, which
 * reads as a rendering fault rather than as a partial undo.
 *
 * @returns {{overlays:Array, summary:object}|null}
 */
export function buildSk360(barData, {
  isAdmin = false, anchorIndex = null, extendedDates = null,
} = {}) {
  if (!isAdmin) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;

  const { dates, ohlc } = barData;
  if (dates.length === 0) return null;

  // Falls back to the last bar so the tool is useful on first press; it is still ONE bar and
  // never a search. The caller says which was used.
  const i = Number.isInteger(anchorIndex) ? anchorIndex : dates.length - 1;
  if (i < 0 || i >= dates.length) return null;

  const anchorDate = isoDay(dates[i]);
  // ⚠ [open, CLOSE, low, high] — the tuple order this chart uses. Reading [3] would anchor every
  // projection to the bar's high and look almost right.
  const bar = ohlc[i];
  const targets = sk360Targets(Array.isArray(bar) ? bar[1] : null);
  if (!anchorDate || !targets) return null;

  // The padded axis, because a year ahead is past the last real bar and has no column otherwise.
  const axis = Array.isArray(extendedDates) && extendedDates.length > dates.length
    ? extendedDates
    : dates;

  const targetDate = sk360TargetDate(anchorDate);
  const terminus = sk360Terminus(axis, dates.length, targetDate);
  // A terminus at or before the anchor has no segment to draw — only reachable if the axis ends
  // before the anchor, which a clicked bar cannot do, so this is a guard rather than a case.
  if (!terminus || terminus.index <= i) return null;

  const drawnDate = axis[terminus.index];
  const drawnUpper = terminus.truncated
    ? sk360PriceAt(i, targets.close, terminus.ideal, targets.upper, terminus.index)
    : targets.upper;
  const drawnLower = terminus.truncated
    ? sk360PriceAt(i, targets.close, terminus.ideal, targets.lower, terminus.index)
    : targets.lower;

  const money = (v) => (Math.abs(v) >= 1000 ? v.toFixed(0) : v.toFixed(2));

  const line = (which, price, lineType, label) => ({
    type: 'trendline',
    // No `source`, so it earns no measurement badge: this is construction geometry, not a
    // measured move. The same reason the recent-pivot projection guide omits it.
    tool: 'sk360',
    date1: dates[i],
    price1: targets.close,
    date2: drawnDate,
    price2: price,
    color: SK360_COLOR,
    lineType,
    lineWidth: SK360_LINE_WIDTH,
    // ⚠ THE END-CAP IS A LABEL, not a symbol. The shared trendline renderer hardcodes
    // `symbol: 'none'` on the series, so a symbol cap would need a change to the renderer every
    // existing trendline goes through. `endLabel` draws a terminating mark AND names the price,
    // which is the more informative of the two and needs no shared change.
    endLabel: label,
    endLabelPosition: 'left',
    id: `${SK360_PREFIX}${which}-${i}`,
  });

  const overlays = [
    line('upper', drawnUpper, SK360_UPPER_LINE_TYPE,
      `SK360 +360° ${money(targets.upper)}${terminus.truncated ? ' ↗' : ''}`),
    line('lower', drawnLower, SK360_LOWER_LINE_TYPE,
      `SK360 −360° ${money(targets.lower)}${terminus.truncated ? ' ↘' : ''}`),
  ];

  return {
    overlays,
    summary: {
      anchorIndex: i,
      anchorDate,
      anchorClose: targets.close,
      root: targets.root,
      upper: targets.upper,
      lower: targets.lower,
      lowerClamped: targets.clamped,
      targetDate,
      drawnDate,
      truncated: terminus.truncated,
      // How many columns short the axis fell. Reported so a regular customer, whose axis stops at
      // their quarter end, is told the target is off-chart rather than shown a shorter line with
      // no explanation.
      shortBy: terminus.truncated ? terminus.ideal - terminus.index : 0,
      usedFallbackAnchor: !Number.isInteger(anchorIndex),
    },
  };
}

/** Whether the tool's overlays are currently on the chart. */
export function hasSk360Overlays(overlays) {
  return Array.isArray(overlays)
    && overlays.some(o => o && typeof o.id === 'string' && o.id.startsWith(SK360_PREFIX));
}

/** The overlay list with this tool's overlays removed. */
export function withoutSk360Overlays(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays.filter(o => !(o && typeof o.id === 'string' && o.id.startsWith(SK360_PREFIX)));
}
